import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle, Users, Search, X, Key } from 'lucide-react';
import { supabase, auth, db } from '../../../lib/supabase';
import { log } from '../../../utils/logger';

const RegistrationForm = ({ _onSuccess, onBackToLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'team_manager'
  });
  
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [teamSearchResults, setTeamSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Registration method - either 'team_search' or 'invite_code'
  const [registrationMethod, setRegistrationMethod] = useState('invite_code');
  const [inviteCode, setInviteCode] = useState('');
  
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [registrationCredentials, setRegistrationCredentials] = useState(null);

  // Fix for mobile viewport white space issue when keyboard hides
  useEffect(() => {
    const handleViewportChange = () => {
      // Force viewport recalculation on mobile when keyboard hides
      if (window.visualViewport) {
        const viewport = window.visualViewport;
        const handleResize = () => {
          // Delay to ensure keyboard is fully hidden
          setTimeout(() => {
            // Force reflow by briefly changing and restoring a style property
            document.documentElement.style.height = 'auto';
            requestAnimationFrame(() => {
              document.documentElement.style.height = '';
              // Also trigger a scroll to ensure proper positioning
              window.scrollTo(0, window.scrollY);
            });
          }, 150);
        };
        
        viewport.addEventListener('resize', handleResize);
        return () => {
          viewport.removeEventListener('resize', handleResize);
        };
      } else {
        // Fallback for older browsers
        const handleResize = () => {
          setTimeout(() => {
            document.documentElement.style.height = 'auto';
            requestAnimationFrame(() => {
              document.documentElement.style.height = '';
              window.scrollTo(0, window.scrollY);
            });
          }, 150);
        };
        
        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize);
        };
      }
    };

    const cleanup = handleViewportChange();
    return cleanup;
  }, []);

  // Function to search for teams (includes description and sport_activity search)
  const searchTeams = async (query) => {
    if (!query.trim()) {
      setTeamSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    
    try {
      // Search by team name, description, and sport_activity
      // Note: We can't search by manager names in the registration form since
      // users aren't authenticated yet and don't have access to user data
      
      const searchQuery = query.trim();
      const searchWords = searchQuery.toLowerCase().split(/\s+/).filter(word => word.length > 0);
      
      let teamsQuery = supabase
        .from('teams')
        .select('id, name, sport_activity, description, state, division')
        .eq('is_active', true);
      
      // Search strategy based on number of words
      if (searchWords.length === 1) {
        // Single word - simple substring search
        teamsQuery = teamsQuery.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,sport_activity.ilike.%${searchQuery}%,state.ilike.%${searchQuery}%,division.ilike.%${searchQuery}%`);
      } else {
        // Multiple words - search for first and last word (most distinctive)
        const primaryWord = searchWords[0];
        const secondaryWord = searchWords[searchWords.length - 1];
        
        // Build query to find teams containing the primary word and optionally the last word
        let orConditions = `name.ilike.%${primaryWord}%,description.ilike.%${primaryWord}%,sport_activity.ilike.%${primaryWord}%`;
        
        if (secondaryWord && secondaryWord !== primaryWord) {
          // Also search for the last word (often the most specific, like "B1")
          orConditions += `,name.ilike.%${secondaryWord}%,division.ilike.%${secondaryWord}%`;
        }
        
        teamsQuery = teamsQuery.or(orConditions);
      }
      
      const { data, error } = await teamsQuery
        .order('name')
        .limit(1000); // Same limit as TeamManagerAccessRequest for comprehensive coverage
        
      if (error) throw error;
      
      // If we hit the limit and have multiple words, do additional searches to capture more results
      let allTeams = data || [];
      if (searchWords.length > 1 && allTeams.length === 1000) {
        log.info('Hit team limit in registration, doing additional searches for better coverage...');
        
        // Search for other word combinations we might have missed
        const additionalSearches = [];
        
        // If we have 3+ words, search for middle words too
        if (searchWords.length >= 3) {
          for (let i = 1; i < searchWords.length - 1; i++) {
            const middleWord = searchWords[i];
            additionalSearches.push(
              supabase
                .from('teams')
                .select('id, name, sport_activity, description, state, division')
                .eq('is_active', true)
                .or(`name.ilike.%${middleWord}%,description.ilike.%${middleWord}%,sport_activity.ilike.%${middleWord}%,state.ilike.%${middleWord}%,division.ilike.%${middleWord}%`)
                .order('name')
                .limit(300)
            );
          }
        }
        
        // Do more targeted searches to find teams we might have missed
        if (searchWords.length >= 2) {
          const firstWord = searchWords[0];
          const lastWord = searchWords[searchWords.length - 1];
          
          // Search for teams where words might appear in different combinations/fields
          // Strategy 1: Look for teams where both words appear in non-name fields
          additionalSearches.push(
            supabase
              .from('teams')
              .select('id, name, sport_activity, description, state, division')
              .eq('is_active', true)
              .or(`description.ilike.%${firstWord}%,description.ilike.%${lastWord}%,division.ilike.%${firstWord}%,division.ilike.%${lastWord}%`)
              .order('name')
              .limit(300)
          );
          
          // Strategy 2: Search for exact phrases or close combinations
          if (firstWord !== lastWord) {
            const phraseSearch = `${firstWord} ${lastWord}`;
            additionalSearches.push(
              supabase
                .from('teams')
                .select('id, name, sport_activity, description, state, division')
                .eq('is_active', true)
                .or(`name.ilike.%${phraseSearch}%,description.ilike.%${phraseSearch}%`)
                .order('name')
                .limit(200)
            );
          }
          
          // Strategy 3: Search with different ordering (by description instead of name)
          // This might catch teams that were cut off in the name-ordered search
          additionalSearches.push(
            supabase
              .from('teams')
              .select('id, name, sport_activity, description, state, division')
              .eq('is_active', true)
              .or(`name.ilike.%${firstWord}%,name.ilike.%${lastWord}%`)
              .order('description')
              .limit(200)
          );
        }
        
        // Execute additional searches in parallel
        if (additionalSearches.length > 0) {
          try {
            const additionalResults = await Promise.all(additionalSearches);
            const additionalTeams = additionalResults
              .filter(result => !result.error)
              .flatMap(result => result.data || []);
            
            log.info(`Found ${additionalTeams.length} additional teams from ${additionalSearches.length} searches`);
            
            // Merge results, removing duplicates based on team ID
            const teamIds = new Set(allTeams.map(t => t.id));
            const newTeams = additionalTeams.filter(team => !teamIds.has(team.id));
            
            log.info(`Original teams: ${allTeams.length}, Additional teams: ${additionalTeams.length}, New unique teams: ${newTeams.length}`);
            
            allTeams = [...allTeams, ...newTeams];
            
            log.info(`Total teams after merging: ${allTeams.length}`);
          } catch (err) {
      // Error caught and ignored
            log.warn('Additional searches failed:', err);
            // Continue with original results
          }
        }
      }
      
      // Client-side filtering and scoring
      let scoredResults = allTeams.map(team => {
        const teamNameLower = (team.name || '').toLowerCase();
        const teamDesc = (team.description || '').toLowerCase();
        const teamSport = (team.sport_activity || '').toLowerCase();
        const teamState = (team.state || '').toLowerCase();
        const teamDivision = (team.division || '').toLowerCase();
        
        let score = 0;
        let matches = true;
        
        if (searchWords.length === 1) {
          // Single word search - already filtered by database
          matches = true;
          score = teamNameLower.includes(searchWords[0]) ? 100 : 50;
        } else {
          // Multi-word search - check if all words appear somewhere
          const allText = `${teamNameLower} ${teamDesc} ${teamSport} ${teamState} ${teamDivision}`;
          
          // Check if all words exist in the team data
          matches = searchWords.every(word => allText.includes(word));
          
          if (matches) {
            // Score based on where and how the words appear
            // Highest score for exact phrase match in name
            if (teamNameLower.includes(searchQuery.toLowerCase())) {
              score = 200;
            }
            // High score if all words are in the name
            else if (searchWords.every(word => teamNameLower.includes(word))) {
              score = 150;
              // Bonus for words appearing in order
              const wordsInOrder = searchWords.join('.*');
              if (new RegExp(wordsInOrder).test(teamNameLower)) {
                score = 175;
              }
            }
            // Medium score if some words in name, others elsewhere
            else if (searchWords.some(word => teamNameLower.includes(word))) {
              score = 100;
            }
            // Lower score if words only appear in other fields
            else {
              score = 50;
            }
          }
        }
        
        return { ...team, searchScore: score, matches };
      });
      
      // Filter to only matching teams and sort by score
      let filteredResults = scoredResults
        .filter(team => team.matches)
        .sort((a, b) => {
          // Sort by score first, then alphabetically
          if (a.searchScore !== b.searchScore) {
            return b.searchScore - a.searchScore;
          }
          return (a.name || '').localeCompare(b.name || '');
        });

      log.info('Registration teams after filtering and scoring:', filteredResults.length);

      // Final deduplication to prevent React key warnings
      const finalTeamIds = new Set();
      filteredResults = filteredResults.filter(team => {
        if (finalTeamIds.has(team.id)) {
          log.warn(`Removing duplicate team in registration: ${team.name} (ID: ${team.id})`);
          return false;
        }
        finalTeamIds.add(team.id);
        return true;
      });
      
      // Limit to 10 results for display
      filteredResults = filteredResults.slice(0, 10);
      
      log.info(`Final registration results after deduplication: ${filteredResults.length}`);
      
      setTeamSearchResults(filteredResults);
    } catch (err) {
      // Error caught and ignored
      log.error('Error searching teams:', err);
      setTeamSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle team search input change
  const handleTeamSearchChange = (e) => {
    const query = e.target.value;
    setTeamSearchQuery(query);
    
    // Clear selected team if search query changes
    if (selectedTeam && query !== selectedTeam.name) {
      setSelectedTeam(null);
    }
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      searchTeams(query);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };
  
  // Handle team selection
  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    setTeamSearchQuery(team.name);
    setTeamSearchResults([]);
    
    // Clear team error if it exists
    if (errors.team) {
      setErrors(prev => ({ ...prev, team: '' }));
    }
  };

  // Handle invite code input change (without real-time validation)
  const handleInviteCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setInviteCode(code);
    
    // Clear previous validation errors
    if (errors.inviteCode) {
      setErrors(prev => ({ ...prev, inviteCode: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Phone validation (optional but if provided, should be valid)
    if (formData.phone && !/^\(\d{3}\)\s\d{3}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = 'Phone should be in format (123) 456-7890';
    }
    
    // Team validation for team managers
    if (formData.role === 'team_manager') {
      if (registrationMethod === 'team_search' && !selectedTeam) {
        newErrors.team = 'Please select a team';
      } else if (registrationMethod === 'invite_code') {
        if (!inviteCode.trim()) {
          newErrors.inviteCode = 'Please enter an invite code';
        } else if (inviteCode.trim().length !== 12) {
          newErrors.inviteCode = 'Invite code must be exactly 12 characters';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format phone number as user types
    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = cleaned;
      
      if (cleaned.length >= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      } else if (cleaned.length >= 3) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      }
      
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // Auto-focus to next field when "Eric" is typed in firstName
      if (name === 'firstName' && value.toLowerCase() === 'eric') {
        // Small delay to ensure the state is updated, then focus the lastName field
        setTimeout(() => {
          const lastNameField = document.querySelector('input[name="lastName"]');
          if (lastNameField) {
            lastNameField.focus();
          }
        }, 100);
      }
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleGoogleSignUp = async () => {
    setMessage('');
    setErrors({});
    try {
      const { error } = await auth.signInWithGoogle();
      if (error) throw error;
    } catch (err) {
      // Error caught and ignored
      setMessage(err.message || 'Google sign up failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage('');
    setErrors({});

    try {
      const registrationData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      };

      let result;

      if (registrationMethod === 'invite_code') {
        // Register with invite code - creates user immediately and adds to team
        result = await db.registerUserWithInvite({
          ...registrationData,
          inviteCode: inviteCode.trim().toUpperCase()
        });
      } else {
        // Register with team selection - creates user and pending team manager request
        result = await db.registerUserWithTeam({
          ...registrationData,
          selectedTeamId: selectedTeam.id
        });
      }

      if (result.error) {
        throw new Error(result.error.message || result.error);
      }

      // Set success message and hide form
      setMessage(result.data.message);
      setIsSubmitted(true);
      
      // Store credentials for continue button (only for invite code users)
      if (registrationMethod === 'invite_code') {
        setRegistrationCredentials({
          email: formData.email,
          password: formData.password
        });
      }

    } catch (error) {
      log.error('Registration error:', error);

      // Check for specific error types and highlight the appropriate field
      if (error.message.includes('duplicate key') || error.message.includes('already exists') || error.message.includes('already registered')) {
        setErrors({ email: 'This email is already registered' });
      } else if (error.message.toLowerCase().includes('invite code')) {
        // Invite code related errors - highlight the invite code field
        let errorMessage = 'Invalid invite code';

        // Provide more specific error messages based on the error
        if (error.message.includes('expired')) {
          errorMessage = 'This invite code has expired';
        } else if (error.message.includes('used') || error.message.includes('already redeemed')) {
          errorMessage = 'This invite code has already been used';
        } else if (error.message.includes('not found') || error.message.includes('does not exist')) {
          errorMessage = 'This invite code does not exist';
        } else if (error.message.includes('12 characters')) {
          errorMessage = 'Invite code must be exactly 12 characters';
        } else {
          // Use the actual error message if it's descriptive
          errorMessage = error.message;
        }

        setErrors({ inviteCode: errorMessage });
      } else {
        setMessage(`Registration failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToDashboard = async () => {
    if (!registrationCredentials) return;
    
    try {
      setLoading(true);
      log.info('🔐 Signing in user after registration...');
      
      const { supabase } = await import('../../../lib/supabase');
      const { error } = await supabase.auth.signInWithPassword({
        email: registrationCredentials.email,
        password: registrationCredentials.password
      });
      
      if (error) {
        log.error('Sign-in error:', error);
        setMessage('Failed to sign in. Please try logging in from the main page.');
        return;
      }
      
      log.info('✅ Sign-in successful, redirecting to dashboard...');
      // The auth state change will handle the redirect automatically
      
    } catch (error) {
      log.error('Continue to dashboard error:', error);
      setMessage('Failed to continue. Please try logging in from the main page.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {!isSubmitted && (
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Create Account</h1>
          <p className="text-gray-600">Join TeamRezy to manage your team reservations</p>
        </div>
      )}

      {!isSubmitted && (
        <div className="mb-6">
          <button
            type="button"
            onClick={handleGoogleSignUp}
            className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
            style={{ border: '1px solid rgb(209, 213, 219)' }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Continue with Google</span>
          </button>
          
          <div className="mt-4 mb-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or register with email</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {message && isSubmitted && (
        <div className="text-center">
          <div className="mb-6 p-8 rounded-xl bg-white border-2 border-green-400 shadow-lg">
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {registrationMethod === 'invite_code' ? 'Welcome to TeamRezy!' : 'Request Submitted!'}
            </h2>
            <p className="text-lg text-gray-800 leading-relaxed font-medium">
              {message}
            </p>
            <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-800 font-semibold">
                What happens next?
              </p>
              {registrationMethod === 'invite_code' ? (
                <>
                  <p className="text-sm text-gray-700 mt-2 font-medium">
                    Click Continue below to access your dashboard and start using TeamRezy immediately!
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleContinueToDashboard}
                      className="w-full bg-navy-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-navy-800 transition-colors duration-200"
                    >
                      Continue to Dashboard
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700 mt-2 font-medium">
                    Your request to join the team has been sent to the team manager for approval.
                  </p>
                  <p className="text-sm text-gray-700 mt-2 font-medium">
                    Once approved and verified, you will receive an email notification and can sign in to start using TeamRezy.
                  </p>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 font-semibold">
                      Ready to sign in?
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      Click "Back to Sign In" below to return to the login page. You'll be able to access your dashboard once your request is approved and you verify your email.
                    </p>
                  </div>
                  <div className="mt-6">
                    <button
                      onClick={() => onBackToLogin()}
                      className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
                    >
                      Back to Sign In
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {message && !isSubmitted && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
          message.includes('successful') 
            ? 'bg-red-50 border border-red-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm text-red-800">{message}</span>
        </div>
      )}

      {!isSubmitted && (
        <form 
          id="registration-form"
          onSubmit={handleSubmit} 
          method="post"
          action="#"
          className="space-y-4"
          data-registration-form="true"
        >
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name *
            </label>
            <div className="relative">
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="John"
                autoComplete="given-name"
              />
            </div>
            {errors.firstName && (
              <p className="text-red-700 text-sm font-semibold mt-1" style={{color: '#dc2626'}}>{errors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <div className="relative">
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Doe"
                autoComplete="family-name"
              />
            </div>
            {errors.lastName && (
              <p className="text-red-700 text-sm font-semibold mt-1" style={{color: '#dc2626'}}>{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <div className="relative">
            <input
              id="registration-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="john.doe@company.com"
              autoComplete="email"
            />
          </div>
          {errors.email && (
            <p className="text-red-700 text-sm font-semibold mt-1" style={{color: '#dc2626'}}>{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <div className="relative">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="(123) 456-7890"
              maxLength="14"
              autoComplete="tel"
            />
          </div>
          {errors.phone && (
            <p className="text-red-700 text-sm font-semibold mt-1" style={{color: '#dc2626'}}>{errors.phone}</p>
          )}
        </div>

        
        {/* Team Information Section (only shown for team managers) */}
        {formData.role === 'team_manager' && (
          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="flex items-center mb-4">
              <Users className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-800">Team Information</h3>
            </div>
            
            {/* Registration Method Selection */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">How would you like to join a team?</p>
              <div className="grid grid-cols-1 gap-3">
                <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="registrationMethod"
                    value="invite_code"
                    checked={registrationMethod === 'invite_code'}
                    onChange={(e) => setRegistrationMethod(e.target.value)}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <Key className="w-5 h-5 text-gray-500" />
                  <div>
                    <div className="font-medium text-gray-800">Use Invite Code</div>
                    <div className="text-sm text-gray-600">Enter a team invite code shared by a team manager</div>
                  </div>
                </label>
                
                <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="registrationMethod"
                    value="team_search"
                    checked={registrationMethod === 'team_search'}
                    onChange={(e) => setRegistrationMethod(e.target.value)}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <Search className="w-5 h-5 text-gray-500" />
                  <div>
                    <div className="font-medium text-gray-800">Search for Team</div>
                    <div className="text-sm text-gray-600">Search and request to join an existing team</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Invite Code Input */}
            {registrationMethod === 'invite_code' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Invite Code *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={handleInviteCodeChange}
                    className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.inviteCode ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter 12-character code"
                    maxLength="12"
                  />
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
                {errors.inviteCode && (
                  <p className="text-red-700 text-sm font-semibold mt-1" style={{color: '#dc2626'}}>{errors.inviteCode}</p>
                )}

                {inviteCode.length === 12 && (
                  <div className="mt-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                      Code will be validated during registration
                    </div>
                  </div>
                )}

                <p className="text-sm text-gray-500 mt-2">
                  Enter the 12-character invite code shared by your team manager.
                </p>
              </div>
            )}

            {/* Team Search */}
            {registrationMethod === 'team_search' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search for Team *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={teamSearchQuery}
                    onChange={handleTeamSearchChange}
                    className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.team ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Search for a team by name"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
                {errors.team && (
                  <p className="text-red-700 text-sm font-semibold mt-1" style={{color: '#dc2626'}}>{errors.team}</p>
                )}
                
                {/* Search Results */}
                {teamSearchResults.length > 0 && !selectedTeam && (
                  <div className="mt-2 border border-gray-200 rounded-lg shadow-sm bg-white max-h-60 overflow-y-auto">
                    <ul className="divide-y divide-gray-200">
                      {teamSearchResults.map(team => (
                        <li 
                          key={team.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelectTeam(team)}
                        >
                          <div className="font-medium text-gray-800">{team.name}</div>
                          {team.sport_activity && (
                            <div className="text-sm text-gray-500">Sport: {team.sport_activity}</div>
                          )}
                          {(team.state || team.division) && (
                            <div className="text-sm text-gray-500">
                              {[team.state, team.division].filter(Boolean).join(', ')}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {isSearching && (
                  <div className="mt-2 text-center py-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-1">Searching teams...</p>
                  </div>
                )}
                
                {/* Selected Team */}
                {selectedTeam && (
                  <div className="mt-3 p-3 border border-green-200 rounded-lg bg-green-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-gray-800">{selectedTeam.name}</div>
                        {selectedTeam.sport_activity && (
                          <div className="text-sm text-gray-600">Sport: {selectedTeam.sport_activity}</div>
                        )}
                        {(selectedTeam.state || selectedTeam.division) && (
                          <div className="text-sm text-gray-600">
                            {[selectedTeam.state, selectedTeam.division].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTeam(null);
                          setTeamSearchQuery('');
                        }}
                        className="text-gray-500 hover:text-gray-700"
                        style={{ border: 'none' }}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
                
                <p className="text-sm text-gray-500 mt-2">
                  Your request to join this team will need to be approved by the team manager.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password *
          </label>
          <div className="relative">
            <input
              id="registration-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full pl-4 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              style={{ border: 'none' }}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-700 text-sm font-semibold mt-1" style={{color: '#dc2626'}}>{errors.password}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password *
          </label>
          <div className="relative">
            <input
              id="registration-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`w-full pl-4 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Confirm your password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              style={{ border: 'none' }}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-700 text-sm font-semibold mt-1" style={{color: '#dc2626'}}>{errors.confirmPassword}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Creating Account...</span>
            </>
          ) : (
            <span>Create Account</span>
          )}
        </button>
        
        {/* Terms and Privacy Notice */}
        <div className="mt-4 text-center">
          <p className="text-gray-500 text-xs">
            By creating an account, you agree to our{' '}
            <a
              href="/terms-of-service.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Terms of Service
            </a>
            {' '}and{' '}
            <a
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
        </form>
      )}

      {/* Back to Login - show after successful submission or always if not submitted */}
      {(isSubmitted || !message) && (
        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            Already have an account?{' '}
            <button
              onClick={onBackToLogin}
              className="text-blue-600 hover:text-blue-700 font-medium"
              style={{ border: 'none' }}
            >
              Sign in here
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;
